import React, { useCallback, useMemo, useState } from 'react';
import type { MenuProps } from 'antd';
import { Button, Dropdown } from 'antd';
import {
  faDownload,
  faFileCsv,
  faFileExcel,
  faFilePdf,
  faFileWord,
  Icon,
} from '../Icon';
import { exportToFile, type IDownloadTypes } from './exportToFile';
import {
  CustomColumns,
  ExtraTableExportData,
  TableExportOptions,
} from './types';

function resolveExtraExportData<T>(result: ExtraTableExportData<T>): {
  extraData: T[];
  defaultData?: unknown[];
} {
  return Array.isArray(result) ? { extraData: result } : result;
}

export interface TableExportButtonProps<T> {
  columns: CustomColumns<T>;
  dataSource: T[];
  exportOptions: TableExportOptions<T>;
  disabled?: boolean;
}

export function TableExportButton<T>({
  columns,
  dataSource,
  exportOptions,
  disabled,
}: TableExportButtonProps<T>) {
  const extraExports = exportOptions.extraExports;
  const [exportBusy, setExportBusy] = useState(false);

  const exportMenuItems: MenuProps['items'] = useMemo(() => {
    const formats = exportOptions.formats;
    const labels = exportOptions.formatLabels;
    return (
      [
        { key: 'xlsx', label: 'XLSX', icon: <Icon icon={faFileExcel} /> },
        { key: 'csv', label: 'CSV', icon: <Icon icon={faFileCsv} /> },
        { key: 'docx', label: 'DOCX', icon: <Icon icon={faFileWord} /> },
        { key: 'pdf', label: 'PDF', icon: <Icon icon={faFilePdf} /> },
      ] as const
    )
      .filter((item) => !formats || formats.includes(item.key))
      .map((item) => ({
        key: item.key,
        label: labels?.[item.key] ?? item.label,
        icon: item.icon,
      }));
  }, [exportOptions.formatLabels, exportOptions.formats]);

  const onExport = useCallback(
    async (type: string, extraExportKey?: string) => {
      if (exportBusy) return;
      setExportBusy(true);
      try {
        if (extraExportKey) {
          const extra = extraExports?.find(
            (item) => item.key === extraExportKey,
          );
          if (!extra) return;
          if (extra.skipClientExport) {
            await extra.getExportData(type as IDownloadTypes);
            return;
          }
          const { extraData, defaultData } = resolveExtraExportData(
            await extra.getExportData(type as IDownloadTypes),
          );
          if (defaultData) {
            exportToFile(
              exportOptions.fileName ?? 'download',
              defaultData as T[],
              columns,
              type as IDownloadTypes,
            );
          }
          exportToFile(
            extra.fileName,
            extraData,
            extra.columns,
            type as IDownloadTypes,
          );
          return;
        }

        const list = await exportOptions.getExportData?.(
          type as IDownloadTypes,
        );
        if (exportOptions.skipClientExport) return;
        exportToFile(
          exportOptions.fileName ?? 'download',
          list || dataSource,
          columns,
          type as IDownloadTypes,
        );
      } finally {
        setExportBusy(false);
      }
    },
    [columns, dataSource, extraExports, exportBusy, exportOptions],
  );

  const exportDropdownMenu = useMemo(() => {
    const withExportKey = (exportKey: string): MenuProps['items'] =>
      exportMenuItems.map((item) => ({
        ...item,
        key: `${exportKey}:${item?.key ?? ''}`,
      }));

    const items: MenuProps['items'] =
      extraExports && extraExports.length > 0
        ? [
            {
              key: 'default',
              label: exportOptions.fileName ?? 'download',
              children: withExportKey('default'),
            },
            ...extraExports.map((extra) => ({
              key: extra.key,
              label: extra.label,
              children: withExportKey(extra.key),
            })),
          ]
        : exportMenuItems;

    return {
      items,
      onClick: (info: { key: string }) => {
        const separatorIndex = info.key.lastIndexOf(':');
        if (separatorIndex === -1) {
          void onExport(info.key);
          return;
        }
        const exportKey = info.key.slice(0, separatorIndex);
        const type = info.key.slice(separatorIndex + 1);
        void onExport(type, exportKey === 'default' ? undefined : exportKey);
      },
    };
  }, [exportMenuItems, extraExports, exportOptions.fileName, onExport]);

  return (
    <Dropdown disabled={disabled || exportBusy} menu={exportDropdownMenu}>
      <Button
        type="text"
        shape="circle"
        aria-label="Download"
        loading={exportBusy}
      >
        <Icon icon={faDownload} style={{ height: 20 }} />
      </Button>
    </Dropdown>
  );
}
